import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, BookOpen, Store, Calendar, Radio, GraduationCap, MessageCircle, ArrowRight, Play, Heart, Shield, BookMarked, Globe } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: Users,
      title: 'Sikh Teachers',
      description: 'Connect with experienced teachers for Gurbani, Kirtan, and Sikh philosophy.',
      to: '/teachers',
      color: 'bg-royal-500',
    },
    {
      icon: BookOpen,
      title: 'Katha Library',
      description: 'Explore thousands of hours of spiritual discourses and teachings.',
      to: '/katha',
      color: 'bg-saffron-500',
    },
    {
      icon: Store,
      title: 'Sikh Accessories',
      description: 'Shop authentic Sikh accessories, turbans, and religious items.',
      to: '/shop',
      color: 'bg-gold-500',
    },
    {
      icon: Calendar,
      title: 'Community Events',
      description: 'Discover local and global Sikh community events and celebrations.',
      to: '/events',
      color: 'bg-green-600',
    },
    {
      icon: Radio,
      title: 'Live Kirtan',
      description: 'Watch live Kirtan from Golden Temple and Gurudwaras worldwide.',
      to: '/live-kirtan',
      color: 'bg-red-500',
    },
    {
      icon: GraduationCap,
      title: 'Sikh School',
      description: 'Learn Gurmukhi, Sikh history, and philosophy through online courses.',
      to: '/courses',
      color: 'bg-purple-600',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-saffron-200">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium text-gray-700">Live Kirtan from Golden Temple</span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-royal-700 leading-tight">
                Your Gateway to{' '}
                <span className="text-saffron-500">Khalsa Sidak</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl">
                Connect, learn, and grow with the global Sikh community. Access live Kirtan, 
                expert teachers, spiritual teachings, and authentic Sikh products.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/live-kirtan">
                  <Button className="btn-primary flex items-center gap-2 text-base" data-testid="cta-live-kirtan">
                    <Play className="w-5 h-5" />
                    Watch Live Kirtan
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button variant="outline" className="btn-outline flex items-center gap-2 text-base" data-testid="cta-courses">
                    Start Learning
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-slide-in hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1614252371231-f6c9b1f99fe9?w=800&auto=format&fit=crop"
                  alt="Golden Temple"
                  className="arch-top w-full h-[500px] object-cover shadow-2xl"
                />
                <div className="absolute inset-0 arch-top bg-gradient-to-t from-royal-900/40 to-transparent"></div>
                
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl border border-saffron-100 animate-pulse-glow">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-saffron-100 rounded-full flex items-center justify-center">
                      <Radio className="w-6 h-6 text-saffron-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Golden Temple</p>
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <span className="live-indicator"></span>
                        Live Now
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-saffron-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-royal-200/30 rounded-full blur-3xl"></div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
              Explore Our Platform
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to connect with your faith and community, all in one place.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Link key={feature.to} to={feature.to}>
                <Card 
                  className="card-hover h-full bg-white border-gray-100 hover:border-saffron-200"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center`}>
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-saffron-600 font-medium pt-2">
                      <span>Explore</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI Chatbot Section */}
      <section className="py-20 bg-gradient-to-br from-royal-600 to-royal-800" data-testid="chatbot-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">AI-Powered Learning</span>
              </div>
              
              <h2 className="font-heading text-4xl md:text-5xl font-bold leading-tight">
                Got Questions About Sikhism?
              </h2>
              
              <p className="text-lg text-royal-100 leading-relaxed">
                Our AI assistant is trained on Sikh scriptures, history, and philosophy. 
                Ask anything about Gurbani, the Gurus, or Sikh traditions.
              </p>
              
              <Link to="/chatbot">
                <Button className="bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-8 py-3 text-lg" data-testid="cta-chatbot">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Start Conversation
                </Button>
              </Link>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-2xl">
              <div className="space-y-4">
                <div className="chat-ai p-4 max-w-[80%]">
                  <p className="text-sm">Waheguru Ji Ka Khalsa, Waheguru Ji Ki Fateh! How can I help you learn about Sikhism today?</p>
                </div>
                <div className="chat-user p-4 max-w-[80%] ml-auto">
                  <p className="text-sm">What is the significance of Japji Sahib?</p>
                </div>
                <div className="chat-ai p-4 max-w-[80%]">
                  <p className="text-sm">Japji Sahib is the opening prayer of Sri Guru Granth Sahib Ji, composed by Guru Nanak Dev Ji. It contains the essence of Sikh philosophy...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-t border-gray-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '50+', label: 'Expert Teachers' },
              { value: '1000+', label: 'Katha Videos' },
              { value: '24/7', label: 'Live Kirtan' },
              { value: '10K+', label: 'Community Members' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-saffron-500">{stat.value}</p>
                <p className="text-gray-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Khalsa Sidak Section */}
      <section className="py-20 bg-gradient-to-br from-cream-100 to-saffron-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-saffron-100 px-4 py-2 rounded-full">
                <Heart className="w-4 h-4 text-saffron-600" />
                <span className="text-sm font-medium text-saffron-700">About Our Mission</span>
              </div>
              
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 leading-tight">
                What is <span className="text-saffron-500">Khalsa Sidak</span>?
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  <strong className="text-royal-700">Khalsa Sidak</strong> is a dedicated digital platform and 
                  <strong className="text-saffron-600"> Sikh accessories shop</strong> designed to support the 
                  <strong className="text-saffron-600"> Sikh community</strong> with learning, guidance, spiritual updates, and authentic religious items.
                </p>
                <p>
                  Our platform helps users stay connected with Sikh values, education, and community initiatives 
                  in an easy and accessible way. Whether you want to learn Gurbani, watch live Kirtan from historic 
                  Gurudwaras, connect with experienced teachers, or shop for authentic Sikh accessories like 
                  <strong className="text-royal-700"> Dastar, Kirpan, Kara, and Kanga</strong> - we bring everything to your fingertips.
                </p>
                <p>
                  The app aims to promote <strong className="text-royal-700">knowledge</strong>, <strong className="text-royal-700">discipline</strong>, 
                  and <strong className="text-royal-700">unity</strong> through modern technology, making Sikh teachings and religious items accessible 
                  to the global Sangat regardless of location.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/collaborate">
                  <Button className="btn-primary">
                    Join Our Community
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="btn-outline">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { 
                  icon: BookMarked, 
                  title: 'Spiritual Learning', 
                  desc: 'Access thousands of Katha, Kirtan, and Gurbani explanations from renowned scholars.',
                  color: 'bg-royal-500'
                },
                { 
                  icon: Globe, 
                  title: 'Global Community', 
                  desc: 'Connect with Sikhs worldwide. Learn, share, and grow together as one Sangat.',
                  color: 'bg-saffron-500'
                },
                { 
                  icon: Shield, 
                  title: 'Authentic Content', 
                  desc: 'All content is carefully curated to ensure authenticity and respect for Sikh traditions.',
                  color: 'bg-green-600'
                },
                { 
                  icon: Heart, 
                  title: 'Seva Spirit', 
                  desc: 'Built with love and devotion to serve the Panth and spread Guru\'s message.',
                  color: 'bg-purple-600'
                },
              ].map((item, index) => (
                <Card key={index} className="bg-white border-gray-100 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5 space-y-3">
                    <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement Banner */}
      <section className="py-12 bg-royal-700" data-testid="mission-banner">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl font-heading text-white leading-relaxed">
            "ਸਰਬੱਤ ਦਾ ਭਲਾ" <span className="text-saffron-400">-</span> <span className="text-saffron-300">Welfare of All</span>
          </p>
          <p className="text-royal-200 mt-4 max-w-2xl mx-auto">
            Khalsa Sidak is committed to spreading the universal message of Guru Nanak Dev Ji - 
            equality, compassion, and selfless service to humanity.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
